// ui/services/trueTodoAdapter.ts
// ✅ 覆蓋全文
//
// Purpose
// - Normalize heterogeneous "todo-like" documents into ONE canonical TrueTodoItem shape.
// - Provide a consistent excluded payload (TrueTodoExcluded) for debug / KPI summary.
//
// Design notes
// - This adapter is used by ui/services/trueTodoAggregator.ts
// - NOT_STARTED 一律算 kevin（法務）
// - 支援 COO/GM demo 帳號（coo@lg.app / psd@lg.app）
// - 避免型別彼此不相容：本檔輸出 TrueTodoItem 直接對齊 ui/types/trueTodo.ts 的定義

import type { TrueTodoItem as CanonicalTrueTodoItem } from "../types/trueTodo";

export type TrueTodoItem = CanonicalTrueTodoItem;

// --- Excluded payload (for debug) ---
export type TrueTodoExcluded = {
  source: string;
  sourceId: string;
  reason: string;
  meta?: Record<string, any>;
  raw?: any;
};

// In aggregator, pushExcluded(ex) is used.
type PushExcluded = (ex: TrueTodoExcluded) => void;

type TrueTodoSource = string;
type TrueTodoModule = string;
type TrueTodoUrgencyBucket = string;

type TargetUser = { userKey: string; label?: string };

const KEY_KEVIN = "kevin";
const KEY_OPS = "ops";
const KEY_GM = "gm";

const SRC_RAW: TrueTodoSource = "RAW_TODO";

function upper(v: any): string {
  return String(v ?? "").toUpperCase();
}

function asMs(ts: any): number | null {
  if (!ts) return null;
  if (typeof ts === "number") return ts;
  if (typeof (ts as any)?.toMillis === "function") return (ts as any).toMillis();
  if (typeof (ts as any)?.seconds === "number") return (ts as any).seconds * 1000;
  const d = new Date(ts);
  const ms = d.getTime();
  return Number.isFinite(ms) ? ms : null;
}

function daysBetween(fromMs: number, toMs: number): number {
  const diff = Math.max(0, toMs - fromMs);
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function inferUrgencyBucket(daysOpen: number): TrueTodoUrgencyBucket {
  if (daysOpen >= 7) return "RED";
  if (daysOpen >= 4) return "YELLOW";
  return "GREEN";
}

function normalizeUserKey(rawKey: any): string | null {
  const s = String(rawKey ?? "").trim();
  if (!s) return null;

  const t = upper(s);

  // ✅ email 對應（demo 帳號）
  if (t.startsWith("COO@LG.APP")) return KEY_OPS;
  if (t.startsWith("PSD@LG.APP")) return KEY_GM;

  // ✅ 角色字樣
  if (t.includes("營運長") || t.includes("財務長") || t.includes("COO") || t.includes("OPERATION")) return KEY_OPS;
  if (t.includes("總經理") || t.includes("GM") || t.includes("PRESIDENT") || t.includes("CEO") || t.includes("PSD"))
    return KEY_GM;

  // ✅ 法務
  if (t.includes("法務") || t.includes("LEGAL") || t.includes("LAW")) return KEY_KEVIN;
  if (t === "KEVIN") return KEY_KEVIN;
  if (t === "OPS") return KEY_OPS;
  if (t === "GM") return KEY_GM;

  return null;
}

function normalizeTargetUsers(input: any): TargetUser[] {
  const out: TargetUser[] = [];

  const pushOne = (v: any) => {
    if (!v) return;
    if (typeof v === "string") {
      const k = normalizeUserKey(v);
      if (k) out.push({ userKey: k });
      return;
    }
    const k = normalizeUserKey(v.userKey ?? v.email ?? v.assignee ?? v.owner ?? v.dept ?? v.role);
    if (k) out.push({ userKey: k, label: v.label ?? v.name ?? v.displayName });
  };

  if (Array.isArray(input)) input.forEach(pushOne);
  else pushOne(input);

  const seen = new Set<string>();
  return out.filter((u) => {
    if (!u?.userKey) return false;
    if (seen.has(u.userKey)) return false;
    seen.add(u.userKey);
    return true;
  });
}

function looksLikePlaceholder(raw: any): boolean {
  const title = String(raw?.title ?? raw?.name ?? "").trim();
  const docNo = String(raw?.docNo ?? raw?.docNumber ?? raw?.serial ?? "").trim();
  return !title && !docNo;
}

function normalizeModule(raw: any): TrueTodoModule {
  const m = upper(raw?.module ?? raw?.todoModule ?? raw?.type ?? raw?.category);

  if (m.includes("CONTRACT")) return "CONTRACT_REVIEW";
  if (m.includes("TEMPLATE") || m.includes("範本")) return "TEMPLATE";
  if (m.includes("SEAL") || m.includes("用印")) return "SEAL";
  if (m.includes("GOV") || m.includes("公文") || m.includes("OFFICIAL") || m.includes("REPLY") || m.includes("函覆") || m.includes("來文")) return "GOV_LETTER";
  if (m.includes("DISPUTE") || m.includes("CASE") || m.includes("案件")) return "CASE_DISPUTE";
  if (m.includes("BRAND") || m.includes("IP") || m.includes("商標") || m.includes("品牌")) return "BRAND_ASSET";
  if (m.includes("COMPLIANCE") || m.includes("法遵")) return "COMPLIANCE_INTEL";
  if (m.includes("PROJECT") || m.includes("專案")) return "PROJECT_CONTROL";
  if (m.includes("GOVERNANCE") || m.includes("核心") || m.includes("WORKSPACE")) return "GOVERNANCE_WORKSPACE";

  return "UNKNOWN";
}

// ---- ✅ 核心：NOT_STARTED 一律算 kevin ----
function derivePendingKeysFromFlowLike(flowLike: any): string[] {
  const overallStatus = upper(flowLike?.status ?? flowLike?.state ?? flowLike?.approvalStatus);

  // ✅ 整體 NOT_STARTED → 直接算 kevin
  if (overallStatus === "NOT_STARTED") return [KEY_KEVIN];

  const stages: any[] = Array.isArray(flowLike?.stages)
    ? flowLike.stages
    : Array.isArray(flowLike?.flowStages)
      ? flowLike.flowStages
      : [];

  const currentKey = String(flowLike?.currentStageKey ?? flowLike?.stageKey ?? flowLike?.currentStage ?? "").trim();

  const fromStageKey = (stageKey: string, stageStatus?: any): string[] => {
    const st = upper(stageStatus);

    // ✅ stage NOT_STARTED → 也直接算 kevin（避免尚未開始卻被導去 COO/GM）
    if (st === "NOT_STARTED") return [KEY_KEVIN];

    const k = upper(stageKey);
    if (!k) return [];

    if (k.includes("LEGAL")) return [KEY_KEVIN];

    // CFO/OPS/COO
    if (k.includes("CFO") || k.includes("COO") || k.includes("OPS") || k.includes("OPERATION") || k.includes("MANAGER_OPS"))
      return [KEY_OPS];

    // GM/PRES/CEO
    if (k.includes("GM") || k.includes("PRES") || k.includes("CEO") || k.includes("MANAGER_GM")) return [KEY_GM];

    return [];
  };

  // 1) 以 currentStageKey 優先
  if (currentKey) {
    const match = stages.find((s) => String(s?.stageKey ?? s?.key ?? "").trim() === currentKey);
    const keys = fromStageKey(currentKey, match?.status ?? match?.state);
    if (keys.length) return keys;
  }

  // 2) 找第一個 pending/in_progress/waiting
  const pending = stages.find((s) => {
    const st = upper(s?.status ?? s?.state);
    return st === "PENDING" || st === "IN_PROGRESS" || st === "WAITING";
  });

  if (pending) {
    const keys = fromStageKey(String(pending?.stageKey ?? pending?.key ?? ""), pending?.status ?? pending?.state);
    if (keys.length) return keys;
  }

  // 保底：不要讓待辦消失
  return [KEY_KEVIN];
}

function buildKey(source: TrueTodoSource, sourceId: string): string {
  return `${String(source)}:${String(sourceId)}`;
}

function buildTrueTodoItem(raw: any, source: TrueTodoSource, sourceId: string, pushExcluded?: PushExcluded): TrueTodoItem | null {
  if (!raw || looksLikePlaceholder(raw)) {
    pushExcluded?.({
      source,
      sourceId,
      reason: "placeholder_or_empty",
      raw,
    });
    return null;
  }

  const title = String(raw?.title ?? raw?.name ?? raw?.subject ?? "").trim() || "(未命名)";
  const docNo = String(raw?.docNo ?? raw?.docNumber ?? raw?.serial ?? raw?.caseNo ?? "").trim();

  const createdAtMs = asMs(raw?.createdAt ?? raw?.createdTime ?? raw?.created_at) ?? Date.now();
  const updatedAtMs = asMs(raw?.updatedAt ?? raw?.updatedTime ?? raw?.updated_at) ?? createdAtMs;
  const nowMs = Date.now();
  const daysOpen = daysBetween(createdAtMs, nowMs);

  const explicitTargets =
    normalizeTargetUsers(raw?.targetUsers)
      .concat(normalizeTargetUsers(raw?.assignees))
      .concat(normalizeTargetUsers(raw?.assignee))
      .concat(normalizeTargetUsers(raw?.owner))
      .concat(normalizeTargetUsers(raw?.handler));

  const derivedKeys = derivePendingKeysFromFlowLike(raw);
  const derivedTargets: TargetUser[] = derivedKeys.map((k) => ({ userKey: k }));

  const targetUsers = (() => {
    const merged = [...explicitTargets, ...derivedTargets];
    const seen = new Set<string>();
    return merged.filter((u) => {
      if (!u?.userKey) return false;
      if (seen.has(u.userKey)) return false;
      seen.add(u.userKey);
      return true;
    });
  })();

  const key = buildKey(source, sourceId);

  // ✅ cast：讓 adapter 輸出型別 = 專案 canonical TrueTodoItem
  return ((
    {
      key,
      id: String(raw?.id ?? raw?._id ?? sourceId ?? key),
      source,
      sourceId,
      module: normalizeModule(raw),
      title,
      docNo,
      createdAtMs,
      updatedAtMs,
      daysOpen,
      urgencyBucket: inferUrgencyBucket(daysOpen),
      targetUsers,
      _debug: {
        status: raw?.status ?? raw?.state,
        currentStageKey: raw?.currentStageKey ?? raw?.stageKey,
      },
    } as unknown
  ) as TrueTodoItem);
}

// -------------------- Exports --------------------

export function adaptModuleDocs(docs: any[], pushExcluded: PushExcluded, source: string = "MODULE"): TrueTodoItem[] {
  const out: TrueTodoItem[] = [];
  for (const d of docs ?? []) {
    const id = String(d?.id ?? d?._id ?? d?.docId ?? d?.refId ?? d?.requestId ?? d?.flowId ?? "");
    const item = buildTrueTodoItem(d, source, id || "module", pushExcluded);
    if (item) out.push(item);
  }
  return out;
}

export function adaptRawTodoDocs(docs: any[], pushExcluded: PushExcluded): TrueTodoItem[] {
  const out: TrueTodoItem[] = [];
  for (const d of docs ?? []) {
    const id = String(d?.id ?? d?._id ?? d?.docId ?? d?.refId ?? "");
    const item = buildTrueTodoItem(d, SRC_RAW, id || "raw", pushExcluded);
    if (item) out.push(item);
  }
  return out;
}

export function adaptApprovalRequestDocs(
  docs: any[],
  pushExcluded: PushExcluded,
  flowById?: Record<string, any>
): TrueTodoItem[] {
  const out: TrueTodoItem[] = [];
  for (const d of docs ?? []) {
    const id = String(d?.id ?? d?._id ?? d?.requestId ?? d?.docId ?? "");
    const flowId = String((d as any)?.approvalFlowId ?? (d as any)?.flowId ?? (d as any)?.approval_flow_id ?? "");
    const merged = flowId && flowById?.[flowId] ? { ...d, ...flowById[flowId] } : d;
    const item = buildTrueTodoItem(merged, "APPROVAL_REQUEST", id || "approval_request", pushExcluded);
    if (item) out.push(item);
  }
  return out;
}

export function adaptApprovalFlowDocs(
  docs: any[],
  pushExcluded: PushExcluded,
  requestById?: Record<string, any>
): TrueTodoItem[] {
  const out: TrueTodoItem[] = [];
  for (const d of docs ?? []) {
    const id = String(d?.id ?? d?._id ?? d?.flowId ?? d?.docId ?? "");
    const targetId = String((d as any)?.targetId ?? (d as any)?.requestId ?? (d as any)?.docId ?? "");
    const merged = targetId && requestById?.[targetId] ? { ...requestById[targetId], ...d } : d;
    const item = buildTrueTodoItem(merged, "APPROVAL_FLOW", id || "approval_flow", pushExcluded);
    if (item) out.push(item);
  }
  return out;
}

export function dedupeTrueTodos(items: TrueTodoItem[]): TrueTodoItem[] {
  const byKey = new Map<string, TrueTodoItem>();

  const keyOf = (t: any) => String(t?.key ?? "");

  for (const it of items ?? []) {
    const k = keyOf(it) || String((it as any)?.id ?? "");
    if (!k) continue;

    const prev = byKey.get(k);

    if (!prev) {
      byKey.set(k, it);
      continue;
    }

    const mergedTargets = (() => {
      const seen = new Set<string>();
      const merged = [...(((prev as any).targetUsers ?? []) as any[]), ...(((it as any).targetUsers ?? []) as any[])];
      return merged.filter((u: any) => {
        const uk = u?.userKey;
        if (!uk) return false;
        if (seen.has(uk)) return false;
        seen.add(uk);
        return true;
      });
    })();

    const prevUpdated = Number((prev as any).updatedAtMs ?? 0);
    const itUpdated = Number((it as any).updatedAtMs ?? 0);

    const keep = itUpdated >= prevUpdated ? it : prev;
    const loser = itUpdated >= prevUpdated ? prev : it;

    const next: TrueTodoItem = (({
      ...(keep as any),
      targetUsers: mergedTargets,
      docNo: (keep as any).docNo || (loser as any).docNo,
      title: String((keep as any).title || "").trim() ? (keep as any).title : (loser as any).title,
    } as unknown) as TrueTodoItem);

    byKey.set(k, next);
  }

  return Array.from(byKey.values());
}
